import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAgentConfig, API_BASE, getUserSettings, updateUserSettings, getAgents } from '../services/api';
import LanguageSelector from './LanguageSelector';
import AgentPicker from './AgentPicker';
import VoicePicker from './VoicePicker';

export default function SettingsPanel({ isOpen, onClose, user, onLogout, instanceStatus, onInstanceStatusChange }) {
  const [agentInfo, setAgentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [activeTab, setActiveTab] = useState('agent');
  
  // Family state
  const [family, setFamily] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [familyName, setFamilyName] = useState('');
  
  // Channel state
  const [channels, setChannels] = useState([]);
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [savingChannel, setSavingChannel] = useState(false);

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentTemplates, setAgentTemplates] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [togglingAgent, setTogglingAgent] = useState(null);

  // Skills state
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [togglingSkill, setTogglingSkill] = useState(null);
  
  // User settings state
  const [userSettings, setUserSettings] = useState({ language: 'en' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadBasicData();
      loadUserSettings();
    }
  }, [isOpen, instanceStatus, onInstanceStatusChange]);
  
  const loadUserSettings = async () => {
    try {
      const settings = await getUserSettings();
      setUserSettings(settings);
      setSelectedAgentId(settings.selectedAgentId);
    } catch (err) {
      console.error('Failed to load user settings:', err);
    }
  };
  
  const handleLanguageChange = async (langCode) => {
    setSavingSettings(true);
    try {
      await updateUserSettings({ language: langCode });
      setUserSettings(prev => ({ ...prev, language: langCode, ttsVoiceId: null }));
    } catch (err) {
      console.error('Failed to update language:', err);
    } finally {
      setSavingSettings(false);
    }
  };
  
  const handleVoiceChange = async (voiceId, voice) => {
    setSavingSettings(true);
    try {
      await updateUserSettings({ ttsVoiceId: voiceId });
      setUserSettings(prev => ({ ...prev, ttsVoiceId: voiceId, voiceName: voice?.name }));
    } catch (err) {
      console.error('Failed to update voice:', err);
    } finally {
      setSavingSettings(false);
    }
  };
  
  const handleAgentSwitch = async (agent) => {
    const agentId = agent.agentId || agent.id;
    setSavingSettings(true);
    try {
      await updateUserSettings({ selectedAgentId: agentId });
      setSelectedAgentId(agentId);
      localStorage.setItem('oimy_selected_agent', JSON.stringify(agent));
    } catch (err) {
      console.error('Failed to switch agent:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    if (isOpen && activeTab === 'agents') {
      loadAgents();
    } else if (isOpen && activeTab === 'skills') {
      loadSkills();
    }
  }, [isOpen, activeTab]);

  const loadBasicData = () => {
    if (instanceStatus) {
      setAgentInfo(instanceStatus);
      setLoading(false);
    } else {
      setLoading(true);
      getAgentConfig()
        .then(c => {
          setAgentInfo(c);
          if (onInstanceStatusChange) onInstanceStatusChange(c);
        })
        .catch(() => setAgentInfo(null))
        .finally(() => setLoading(false));
    }
    
    // Load family info
    fetch(`${API_BASE}/api/u/family`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setFamily(d.family);
        setFamilyMembers(d.members || []);
      })
      .catch(() => {});
    
    // Load channels
    fetch(`${API_BASE}/api/u/channels`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setChannels(d.channels || []);
        const tg = d.channels?.find(c => c.channel === 'telegram');
        if (tg) setTelegramChatId(tg.telegramChatId || '');
      })
      .catch(() => {});
  };

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await fetch(`${API_BASE}/api/u/agents`, { credentials: 'include' });
      const data = await res.json();
      setAgents(data.agents || []);
      setAgentTemplates(data.templates || []);
    } catch (e) {
      console.error('Failed to load agents:', e);
    }
    setLoadingAgents(false);
  };

  const loadSkills = async () => {
    setLoadingSkills(true);
    try {
      const res = await fetch(`${API_BASE}/api/u/skills`, { credentials: 'include' });
      const data = await res.json();
      setSkills(data.skills || []);
    } catch (e) {
      console.error('Failed to load skills:', e);
    }
    setLoadingSkills(false);
  };

  const toggleAgent = async (agent) => {
    setTogglingAgent(agent.id);
    try {
      await fetch(`${API_BASE}/api/u/agents`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          familyAgentId: agent.id,
          isEnabled: !agent.isEnabled,
        }),
      });
      setAgents(prev => prev.map(a => 
        a.id === agent.id ? { ...a, isEnabled: !a.isEnabled } : a
      ));
    } catch (e) {
      console.error('Failed to toggle agent:', e);
    }
    setTogglingAgent(null);
  };

  const toggleSkill = async (skill) => {
    setTogglingSkill(skill.id);
    try {
      await fetch(`${API_BASE}/api/u/skills`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skillId: skill.id,
          isEnabled: !skill.userEnabled,
        }),
      });
      setSkills(prev => prev.map(s => 
        s.id === skill.id ? { ...s, userEnabled: !s.userEnabled, isEnabled: s.familyEnabled && !s.userEnabled } : s
      ));
    } catch (e) {
      console.error('Failed to toggle skill:', e);
    }
    setTogglingSkill(null);
  };

  const handleRestart = async () => {
    setRestarting(true);
    try {
      const res = await fetch(`${API_BASE}/api/u/settings/agent/restart`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const updated = { ...agentInfo, status: 'creating' };
        setAgentInfo(updated);
        if (onInstanceStatusChange) onInstanceStatusChange(updated);
      }
    } catch (err) {
      console.error('Restart failed:', err);
    }
    setRestarting(false);
  };

  const handleCreateFamily = async () => {
    setCreatingFamily(true);
    try {
      const res = await fetch(`${API_BASE}/api/u/family`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', familyName: familyName || undefined }),
      });
      const data = await res.json();
      if (res.ok) {
        setFamily({
          id: data.familyId,
          name: data.familyName,
          familyCode: data.familyCode,
          isPrimary: true,
        });
        setFamilyName('');
      } else {
        alert(data.error || 'Failed to create family');
      }
    } catch {
      alert('Failed to create family');
    }
    setCreatingFamily(false);
  };

  const handleShareFamily = () => {
    if (!family?.familyCode) return;
    const message = `Join our family on OiMy! 👨‍👩‍👧‍👦\n\nUse this family code during registration:\n${family.familyCode}\n\nSign up at: https://app.oimyai.com`;
    
    if (navigator.share) {
      navigator.share({ title: 'Join my OiMy family', text: message });
    } else {
      navigator.clipboard.writeText(message);
      alert('Invite message copied to clipboard!');
    }
  };

  const handleSaveChannel = async () => {
    setSavingChannel(true);
    try {
      const res = await fetch(`${API_BASE}/api/u/channels`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'telegram',
          telegramChatId,
          telegramBotToken: telegramBotToken || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        alert('Telegram settings saved!');
        const chRes = await fetch(`${API_BASE}/api/u/channels`, { credentials: 'include' });
        const chData = await chRes.json();
        setChannels(chData.channels || []);
      } else {
        alert(data.error || 'Failed to save');
      }
    } catch {
      alert('Failed to save channel settings');
    }
    setSavingChannel(false);
  };

  const statusColor = agentInfo?.status === 'running' ? '#2ecc71'
    : agentInfo?.status === 'creating' ? '#f39c12'
    : agentInfo?.status === 'error' ? '#ff3c3c'
    : 'rgba(255,255,255,0.3)';

  const statusLabel = agentInfo?.status === 'running' ? 'Connected'
    : agentInfo?.status === 'creating' ? 'Setting up...'
    : agentInfo?.status === 'error' ? 'Error'
    : agentInfo?.status === 'no_instance' ? 'Provisioning...'
    : 'Unknown';

  const showRestart = agentInfo?.status === 'error' || agentInfo?.status === 'stopped';

  const tabStyle = (active) => ({
    flex: 1,
    padding: '8px 6px',
    borderRadius: 10,
    background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
    border: 'none',
    color: active ? 'white' : 'rgba(255,255,255,0.5)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: 14,
    outline: 'none',
  };

  const buttonStyle = {
    padding: '12px 20px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, rgba(167,21,233,0.6), rgba(100,150,255,0.5))',
    border: 'none',
    color: 'white',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const cardStyle = {
    padding: 16,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    marginBottom: 12,
  };

  const toggleStyle = (enabled) => ({
    width: 44,
    height: 24,
    borderRadius: 12,
    background: enabled ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.1)',
    border: '1px solid ' + (enabled ? 'rgba(46,204,113,0.6)' : 'rgba(255,255,255,0.15)'),
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
  });

  const toggleKnobStyle = (enabled) => ({
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: enabled ? '#2ecc71' : 'rgba(255,255,255,0.4)',
    position: 'absolute',
    top: 2,
    left: enabled ? 22 : 2,
    transition: 'all 0.2s',
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            style={{
              position: 'absolute', inset: 0, zIndex: 50,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="settings-panel"
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 51,
              maxHeight: '85vh', overflowY: 'auto',
              background: 'linear-gradient(180deg, #1a1a88 0%, #151266 100%)',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '24px 24px 0 0',
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
            </div>

            <div style={{ padding: '20px 24px 36px' }}>
              {/* User info + logout */}
              {user && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginBottom: 20, padding: '12px 16px', borderRadius: 16,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(167,21,233,0.4), rgba(100,150,255,0.3))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 16, fontWeight: 700, flexShrink: 0,
                  }}>
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.name || 'User'}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.email}
                    </div>
                  </div>
                  <button onClick={onLogout} style={{
                    padding: '6px 14px', borderRadius: 10,
                    background: 'rgba(255,60,60,0.12)', border: '1px solid rgba(255,60,60,0.15)',
                    color: 'rgba(255,100,100,0.8)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', flexShrink: 0,
                  }}>
                    Logout
                  </button>
                </div>
              )}

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 4, overflowX: 'auto' }}>
                <button style={tabStyle(activeTab === 'agent')} onClick={() => setActiveTab('agent')}>🔌 Status</button>
                <button style={tabStyle(activeTab === 'preferences')} onClick={() => setActiveTab('preferences')}>⚙️ Preferences</button>
                <button style={tabStyle(activeTab === 'agents')} onClick={() => setActiveTab('agents')}>🧠 Agents</button>
                <button style={tabStyle(activeTab === 'skills')} onClick={() => setActiveTab('skills')}>🔧 Skills</button>
                <button style={tabStyle(activeTab === 'family')} onClick={() => setActiveTab('family')}>👨‍👩‍👧 Family</button>
                <button style={tabStyle(activeTab === 'channels')} onClick={() => setActiveTab('channels')}>📱 Channels</button>
              </div>
              
              {/* Preferences Tab - Language, Voice, and Agent Selection */}
              {activeTab === 'preferences' && (
                <div>
                  <div style={cardStyle}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
                      🌐 Language
                    </h3>
                    <LanguageSelector 
                      value={userSettings.language}
                      onChange={handleLanguageChange}
                      compact={true}
                      showLabel={false}
                      gridCols={2}
                    />
                  </div>
                  
                  <div style={{ ...cardStyle, marginTop: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: 12 }}>
                      🎤 Voice
                    </h3>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
                      Choose how Oi sounds when speaking
                      {userSettings.voiceName && (
                        <span style={{ color: 'rgba(167,139,250,0.9)', marginLeft: 4 }}>
                          • Currently: {userSettings.voiceName}
                        </span>
                      )}
                    </p>
                    <VoicePicker 
                      language={userSettings.language || 'en'}
                      value={userSettings.ttsVoiceId}
                      onChange={handleVoiceChange}
                      compact={true}
                    />
                  </div>
                  
                  <div style={{ ...cardStyle, marginTop: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
                      🤖 Switch Agent
                    </h3>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                      Choose which agent you want to chat with
                    </p>
                    <AgentPicker 
                      onSelect={handleAgentSwitch}
                      currentAgentId={selectedAgentId}
                      showTitle={false}
                    />
                  </div>
                  
                  {savingSettings && (
                    <p style={{ fontSize: 12, color: 'rgba(167,139,250,0.8)', textAlign: 'center', marginTop: 12 }}>
                      Saving...
                    </p>
                  )}
                </div>
              )}

              {/* Status Tab */}
              {activeTab === 'agent' && (
                <div style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: statusColor,
                      boxShadow: agentInfo?.status === 'running' ? '0 0 8px rgba(46,204,113,0.5)' : 'none',
                    }} />
                    <div style={{
                      fontSize: 14, fontWeight: 600,
                      color: agentInfo?.status === 'running' ? '#2ecc71' : agentInfo?.status === 'error' ? '#ff6b6b' : 'rgba(255,255,255,0.5)',
                    }}>
                      {loading ? 'Loading...' : statusLabel}
                    </div>
                  </div>
                  {agentInfo && agentInfo.status !== 'no_instance' && agentInfo.subdomain && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Subdomain</span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                        {agentInfo.subdomain}.oimyai.com
                      </span>
                    </div>
                  )}
                  {showRestart && (
                    <button onClick={handleRestart} disabled={restarting} style={{
                      ...buttonStyle, width: '100%', marginTop: 12,
                      background: 'rgba(255,255,255,0.1)', opacity: restarting ? 0.5 : 1,
                    }}>
                      {restarting ? 'Restarting...' : '🔄 Restart Agent'}
                    </button>
                  )}
                </div>
              )}

              {/* Agents Tab */}
              {activeTab === 'agents' && (
                <div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
                    Toggle agents on/off. Disabled agents won't respond to you.
                  </p>
                  {loadingAgents ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: 20 }}>Loading...</div>
                  ) : agents.length === 0 ? (
                    <div style={{ ...cardStyle, textAlign: 'center' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>No agents configured yet</div>
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 8 }}>
                        Agents will appear here once your family is set up.
                      </p>
                    </div>
                  ) : (
                    agents.map(agent => (
                      <div key={agent.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 24 }}>{agent.templateIcon || '🤖'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                            {agent.nickname || agent.name}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                            {agent.templateCategory || 'General'}
                          </div>
                        </div>
                        <div
                          style={toggleStyle(agent.isEnabled)}
                          onClick={() => !togglingAgent && toggleAgent(agent)}
                        >
                          <div style={toggleKnobStyle(agent.isEnabled)} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Skills Tab */}
              {activeTab === 'skills' && (
                <div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
                    Enable or disable skills for your AI agents.
                  </p>
                  {loadingSkills ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: 20 }}>Loading...</div>
                  ) : skills.length === 0 ? (
                    <div style={{ ...cardStyle, textAlign: 'center' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>No skills available</div>
                    </div>
                  ) : (
                    skills.map(skill => (
                      <div key={skill.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12, opacity: skill.familyEnabled ? 1 : 0.5 }}>
                        <div style={{ fontSize: 24 }}>{skill.icon || '🔧'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                            {skill.name}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                            {skill.description?.substring(0, 50) || skill.category || 'General'}
                          </div>
                        </div>
                        <div
                          style={{
                            ...toggleStyle(skill.isEnabled),
                            opacity: skill.familyEnabled ? 1 : 0.5,
                            cursor: skill.familyEnabled ? 'pointer' : 'not-allowed',
                          }}
                          onClick={() => skill.familyEnabled && !togglingSkill && toggleSkill(skill)}
                        >
                          <div style={toggleKnobStyle(skill.isEnabled)} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Family Tab */}
              {activeTab === 'family' && (
                <div>
                  {family ? (
                    <div style={cardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: 'white' }}>{family.name}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                            {family.isPrimary ? '👑 Family Admin' : '👤 Member'}
                          </div>
                        </div>
                        <div style={{
                          padding: '8px 14px', borderRadius: 8,
                          background: 'rgba(100,150,255,0.15)',
                          fontFamily: 'monospace', fontSize: 14, color: 'rgba(100,150,255,0.9)',
                        }}>
                          {family.familyCode}
                        </div>
                      </div>
                      {family.isPrimary && (
                        <button onClick={handleShareFamily} style={{ ...buttonStyle, width: '100%', marginBottom: 16, background: 'rgba(255,255,255,0.1)' }}>
                          📤 Share Family Code
                        </button>
                      )}
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                        Family Members ({familyMembers.length})
                      </div>
                      {familyMembers.map(m => (
                        <div key={m.id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 12px', borderRadius: 8, marginBottom: 6,
                          background: 'rgba(255,255,255,0.04)',
                        }}>
                          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>{m.name}</span>
                          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{m.familyRole || 'member'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ ...cardStyle, textAlign: 'center', padding: 24 }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍👩‍👧‍👦</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: 'white', marginBottom: 8 }}>
                        Create a Family Account
                      </div>
                      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 16, lineHeight: 1.5 }}>
                        Share your AI assistant with family members. Everyone gets their own personalized experience.
                      </p>
                      <input
                        type="text"
                        placeholder="Family name (optional)"
                        value={familyName}
                        onChange={e => setFamilyName(e.target.value)}
                        style={{ ...inputStyle, marginBottom: 12, textAlign: 'center' }}
                      />
                      <button
                        onClick={handleCreateFamily}
                        disabled={creatingFamily}
                        style={{ ...buttonStyle, width: '100%', opacity: creatingFamily ? 0.5 : 1 }}
                      >
                        {creatingFamily ? 'Creating...' : '✨ Create Family'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Channels Tab */}
              {activeTab === 'channels' && (
                <div style={cardStyle}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'white', marginBottom: 4 }}>
                    📱 Telegram
                  </div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
                    Connect your Telegram to chat with your AI agent
                  </p>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 6 }}>
                      Your Telegram Chat ID or Username
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 123456789 or @username"
                      value={telegramChatId}
                      onChange={e => setTelegramChatId(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 6 }}>
                      Bot Token (optional - for your own bot)
                    </label>
                    <input
                      type="password"
                      placeholder="From @BotFather"
                      value={telegramBotToken}
                      onChange={e => setTelegramBotToken(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <button
                    onClick={handleSaveChannel}
                    disabled={savingChannel || !telegramChatId}
                    style={{ ...buttonStyle, width: '100%', opacity: (savingChannel || !telegramChatId) ? 0.5 : 1 }}
                  >
                    {savingChannel ? 'Saving...' : '💾 Save Telegram Settings'}
                  </button>
                  {channels.find(c => c.channel === 'telegram')?.isVerified && (
                    <div style={{
                      marginTop: 12, padding: '8px 12px', borderRadius: 8,
                      background: 'rgba(46,204,113,0.1)', textAlign: 'center',
                      color: '#2ecc71', fontSize: 12, fontWeight: 600,
                    }}>
                      ✓ Telegram Connected
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
